import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Home, Loader2, Lock, MailCheck } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/store/auth';
import { api, ApiError } from '@/lib/api';
import { googleAvailable } from '@/lib/google';
import { clearToken, loadTokenBrand, tokenPending, useBrand } from '@/lib/brand';
import { afterLoginPath, safeNext } from '@/lib/brandSources';
import { EMAIL_RE, suggestEmail, useLeadIntake, type LeadIntake } from '@/lib/leadIntake';
import { PORTAL_NAME, dirOf, useDocumentLang, useLang, type PortalLang } from '@/lib/lang';
import { BrandBand } from '@/components/BrandBand';
import { LangSwitch } from '@/components/LangSwitch';
import { Turnstile, turnstileSiteKey } from '@/tour/Turnstile';
import { FRONT_DOOR, type FrontDoorStrings, type Situation } from './frontDoorStrings';

/**
 * The portal's front door: one email field and one button, "send me the link".
 *
 * - Opened with the org hint of an office whose prospect pages are published
 *   (prospect mode): it speaks to someone who wants to live there, takes a new
 *   lead's email with the office's privacy notice and the separate marketing
 *   opt-in, and the same field still works for people who already live there.
 * - Anything else: it re-sends the personal link to people the office has.
 *
 * In Hebrew, Arabic or English (the switch in the top corner; `?lang=ar` from
 * an ad). The language goes with the request: the notice shown is kept with
 * the consent, and the email comes in the same language.
 *
 * Never "sign in", "register", "account" or "password": the visitor asks for a
 * link and gets one. The answer is the same whoever the email belongs to.
 */

/**
 * Where the building's band will be, while its brand is still on the way: a
 * neutral block of the same size, never TendedLoop's logo that would then swap.
 */
function BandSkeleton() {
  return (
    <div className="mb-6" aria-hidden data-testid="band-skeleton">
      <div className="flex min-h-[240px] animate-pulse items-center justify-center bg-slate-100 px-6 pb-10 pt-12">
        <div className="h-16 w-[200px] max-w-full rounded-xl bg-slate-200" />
      </div>
    </div>
  );
}

/** "Continue with Google": the email Google vouches for opens the portal at once. */
function GoogleOption({
  t,
  lead,
  onSignedIn,
}: {
  t: FrontDoorStrings;
  lead?: { consentVersion: string; marketingOptIn: boolean; lang: PortalLang };
  onSignedIn: () => void;
}) {
  const googleSignIn = useAuth((s) => s.googleSignIn);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!googleAvailable()) return null;
  return (
    <div className="mt-6">
      <div className="mb-5 flex items-center gap-3 text-[16px] text-slate-500" aria-hidden>
        <span className="h-px flex-1 bg-slate-200" />
        {t.or}
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <div className={busy ? 'pointer-events-none opacity-60' : ''}>
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={async (res) => {
              if (!res.credential) return setError(t.googleFailed);
              setBusy(true);
              setError('');
              const ok = await googleSignIn(res.credential, lead);
              setBusy(false);
              if (ok) return onSignedIn();
              const code = useAuth.getState().errorCode;
              setError((code && t.googleErrors[code]) || t.googleFailed);
            }}
            onError={() => setError(t.googleFailed)}
            text="continue_with"
            shape="pill"
            width={300}
          />
        </div>
      </div>
      {error && (
        <p className="mt-3 text-start text-[16px] leading-relaxed text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** What went wrong, in the visitor's words, and what to do. */
function messageFor(err: unknown, t: FrontDoorStrings): string {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return t.network;
    if (err.status === 429) return t.tooMany;
    if (err.code === 'RESIDENT_BOT_CHECK') return t.botFailed;
    if (err.code === 'RESIDENT_NOTICE_CHANGED') return t.noticeChanged;
  }
  return t.sendFailed;
}

const RESEND_WAIT_S = 30;

/** The one field and the one button, then "check your email" in the same place. */
function FrontDoor({
  lang,
  situation,
  intake,
  operator,
  hint,
  next,
  onNoticeChanged,
  onSignedIn,
}: {
  lang: PortalLang;
  situation: Situation;
  intake: LeadIntake | null;
  operator: string | null;
  hint: string | null;
  next: string;
  onNoticeChanged: () => void;
  onSignedIn: () => void;
}) {
  const t = FRONT_DOOR[lang];
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'sent'>('form');
  // The address itself (marks the field) vs. a send that did not go through (does not).
  const [fieldError, setFieldError] = useState<'missing' | 'malformed' | ''>('');
  const [error, setError] = useState<((t: FrontDoorStrings) => string) | null>(null);
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const [botToken, setBotToken] = useState<string | null>(null);
  const [botReset, setBotReset] = useState(0);
  const [wait, setWait] = useState(0);
  const [resent, setResent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const sentRef = useRef<HTMLHeadingElement>(null);
  const prospect = !!intake;
  const needsBot = prospect && !!turnstileSiteKey();
  const suggestion = suggestEmail(email);
  const copy = t.situations[situation];

  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);
  useEffect(() => {
    if (phase === 'sent') sentRef.current?.focus();
  }, [phase]);

  async function send(again = false): Promise<void> {
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setFieldError(value ? 'malformed' : 'missing');
      inputRef.current?.focus();
      return;
    }
    if (needsBot && !botToken) {
      setError(() => (s: FrontDoorStrings) => s.botPending);
      return;
    }
    setError(null);
    setFieldError('');
    setResent(false);
    if (!again) setPhase('sending');
    try {
      await api.requestLink(
        value,
        next,
        intake
          ? {
              o: hint ?? undefined,
              consentVersion: intake.consentVersion,
              marketingOptIn: marketing,
              ...(botToken ? { turnstileToken: botToken } : {}),
              website,
              lang,
            }
          : hint
            ? { o: hint, website, lang }
            : { website, lang },
      );
      navigator.vibrate?.([30, 50, 30]);
      setPhase('sent');
      setWait(RESEND_WAIT_S);
      if (again) setResent(true);
    } catch (err) {
      setPhase(again ? 'sent' : 'form');
      setError(() => (s: FrontDoorStrings) => messageFor(err, s));
      if (err instanceof ApiError && err.code === 'RESIDENT_NOTICE_CHANGED') onNoticeChanged();
      if (!again) inputRef.current?.focus();
    } finally {
      if (needsBot) {
        setBotToken(null);
        setBotReset((n) => n + 1);
      }
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  // The bot check speaks Hebrew or English (Arabic pages show its English).
  const bot = needsBot && (
    <Turnstile lang={lang === 'en' ? 'en' : 'he'} action="portal_link" onToken={setBotToken} resetKey={botReset} />
  );
  const errorText = error?.(t) ?? '';
  const fieldErrorText = fieldError === 'missing' ? t.emailMissing : fieldError === 'malformed' ? t.emailMalformed : '';

  if (phase === 'sent') {
    return (
      <section className="w-full text-start" aria-live="polite" data-front-door="sent">
        <div className="mb-4 flex h-14 w-14 animate-[pop_.45s_ease-out] items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
          <MailCheck className="h-7 w-7" aria-hidden />
        </div>
        <h1 ref={sentRef} tabIndex={-1} className="text-[26px] font-bold leading-tight text-brand-900 outline-none">
          {t.sentTitle}
        </h1>
        <div className="mt-2 text-[17px] leading-relaxed text-slate-700" role="status">
          <p>{prospect ? t.sentProspect : t.sentResident}</p>
          {/* The address reads left to right, on the page's own side. */}
          <p className="mt-1 break-all font-semibold text-brand-900">
            <bdi dir="ltr">{email.trim()}</bdi>
          </p>
          {prospect && <p className="mt-1">{t.sentOpen}</p>}
        </div>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-500">{t.sentSpam}</p>
        {bot}
        {errorText && <SendError text={errorText} />}
        {resent && (
          <p className="mt-4 text-[16px] font-semibold text-brand-700" role="status">
            {t.sentAgain}
          </p>
        )}
        <button
          type="button"
          onClick={() => void send(true)}
          disabled={wait > 0}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-xl border-2 border-brand-600 bg-white px-4 text-[16px] font-semibold text-brand-700 disabled:border-slate-200 disabled:text-slate-500"
        >
          {wait > 0 ? t.resendIn(wait) : t.resend}
        </button>
        <button
          type="button"
          onClick={() => {
            setPhase('form');
            setError(null);
            setFieldError('');
            setResent(false);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className="mt-2 min-h-[48px] w-full text-[16px] font-semibold text-slate-600 underline-offset-4 hover:underline"
        >
          {t.changeEmail}
        </button>
        <style>{`@keyframes pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
      </section>
    );
  }

  const sending = phase === 'sending';
  return (
    <div className="w-full text-start" data-front-door={situation}>
      <h1 className="text-[26px] font-bold leading-tight text-brand-900">{copy.title}</h1>
      <p className="mt-2 text-[17px] leading-relaxed text-slate-600">{copy.lead}</p>

      <form onSubmit={submit} noValidate className="mt-6">
        <label htmlFor="front-door-email" className="mb-1.5 block text-[16px] font-semibold text-slate-700">
          {t.emailLabel}
        </label>
        <input
          ref={inputRef}
          id="front-door-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="send"
          dir="ltr"
          maxLength={200}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (fieldError) setFieldError('');
          }}
          aria-invalid={!!fieldError}
          aria-describedby={fieldError ? 'front-door-error' : undefined}
          placeholder="name@example.com"
          className="min-h-[52px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[17px] outline-none transition-colors placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 aria-[invalid=true]:border-red-400"
        />
        {suggestion && (
          <p className="mt-2 text-[16px] text-slate-600">
            {t.didYouMean}
            <button
              type="button"
              onClick={() => setEmail(suggestion)}
              className="min-h-[44px] font-semibold text-brand-700 underline underline-offset-2"
              dir="ltr"
            >
              {suggestion}
            </button>
            {t.didYouMeanEnd}
          </p>
        )}
        {fieldErrorText && <ErrorLine id="front-door-error" text={fieldErrorText} />}

        {/* Honeypot: hidden from people and screen readers; bots fill it. */}
        <div aria-hidden="true" className="absolute -start-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="front-door-website">Website</label>
          <input
            id="front-door-website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        {prospect && operator && (
          <label className="mt-4 flex min-h-[48px] cursor-pointer items-start gap-3 text-[16px] leading-relaxed text-slate-600">
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="mt-1 h-6 w-6 shrink-0 accent-brand-600"
            />
            <span>{t.marketing(operator)}</span>
          </label>
        )}

        {bot}
        {errorText && <SendError text={errorText} />}

        <button
          type="submit"
          disabled={sending}
          className="mt-5 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white shadow-sm transition-transform active:scale-[0.99] disabled:opacity-70"
        >
          {sending && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
          {sending ? t.sending : t.submit}
        </button>
      </form>

      {prospect && <p className="mt-3 text-[16px] leading-relaxed text-slate-600">{t.alreadyLive}</p>}

      {operator && intake && <PrivacyNote t={t} operator={operator} intake={intake} />}

      <GoogleOption
        t={t}
        lead={intake ? { consentVersion: intake.consentVersion, marketingOptIn: marketing, lang } : undefined}
        onSignedIn={onSignedIn}
      />
    </div>
  );
}

/**
 * The operator's own privacy policy, when the office linked one. Our own notice
 * wording (who holds the data, retention, Meta) is withdrawn until the operator
 * decides it; its strings stay in frontDoorStrings.ts for that day.
 */
function PrivacyNote({ t, operator, intake }: { t: FrontDoorStrings; operator: string; intake: LeadIntake }) {
  if (!intake.privacyPolicyUrl) return null;
  return (
    <div className="mt-6 border-t border-slate-200 pt-4 text-[16px] leading-relaxed" data-testid="privacy-note">
      <a
        href={intake.privacyPolicyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex min-h-[48px] items-center font-semibold text-brand-700 underline"
      >
        {t.privacy.policy(operator)}
      </a>
    </div>
  );
}

/** A send that did not go through: what happened and what to do, beside the button (the address is fine). */
function SendError({ text }: { text: string }) {
  return (
    <p
      className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[16px] leading-relaxed text-red-800"
      role="alert"
    >
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      {text}
    </p>
  );
}

function ErrorLine({ text, id }: { text: string; id?: string }) {
  return (
    <p id={id} className="mt-2 text-[16px] leading-relaxed text-red-700" role="alert">
      {text}
    </p>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const expired = params.get('expired') === '1';
  const lang = useLang((s) => s.lang);
  const t = FRONT_DOOR[lang];
  useDocumentLang(lang);
  // Only an internal path (e.g. "/lease"), and the org hint goes along.
  const hint = useBrand((s) => s.hintHandle);
  const nextPath = safeNext(params.get('next'));
  const next = afterLoginPath(nextPath, hint);
  const { verifyLink, loading, error, isAuthenticated } = useAuth();
  const started = useRef(false);
  const [noticeReload, setNoticeReload] = useState(0);
  const intake = useLeadIntake(hint, noticeReload);

  // With a link in the URL, move on only once THAT link has verified: a session
  // already on this device must not carry the resident past a different link.
  // Its office's brand decides this page; asked for only when the link did not
  // open the portal (it then still names its office, valid or expired).
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    tokenPending();
    void verifyLink(token).then((ok) => {
      if (ok) navigate(next, { replace: true });
      else void loadTokenBrand(token);
    });
  }, [token, verifyLink, navigate, next]);
  useEffect(() => () => clearToken(), []);

  useEffect(() => {
    if (!token && isAuthenticated) navigate(next, { replace: true });
  }, [token, isAuthenticated, navigate, next]);

  const verifying = !!token && (loading || (!error && !isAuthenticated));
  const linkFailed = !!token && !loading && !!error;
  // The link's office, else a returning resident's building (kept on this
  // device after a visit), else the link's org hint.
  const brand = useBrand((s) => s.brand);
  const brandLoading = useBrand((s) => s.loading);
  const banded = !!brand || brandLoading;
  const lead = intake.state === 'ready' ? intake.intake : null;
  // Prospect copy only for the office the brand on screen belongs to.
  const operator = brand?.displayName?.trim() || null;
  const situation: Situation = linkFailed ? 'invalid' : expired ? 'expired' : lead ? 'prospect' : 'resident';
  const deciding = !verifying && intake.state === 'loading';

  return (
    <div className="relative min-h-screen" dir={dirOf(lang)} lang={lang}>
      <LangSwitch />
      {brand ? <BrandBand brand={brand} size="hero" /> : brandLoading && <BandSkeleton />}
      <div className={'mx-auto flex max-w-md flex-col px-5 ' + (banded ? 'pb-10 pt-2' : 'min-h-screen pb-10 pt-16')}>
        {!banded && (
          <div className="mb-8 flex items-center gap-2.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500 text-white shadow-md">
              <Home className="h-6 w-6" aria-hidden />
            </div>
            <p className="text-lg font-bold tracking-tight text-brand-900" dir="ltr">
              TendedLoop
            </p>
          </div>
        )}

        {verifying && (
          <div className="flex flex-col items-center py-10 text-center" role="status">
            <Loader2 className="mb-3 h-7 w-7 animate-spin text-brand-500" aria-hidden />
            <p className="text-lg text-brand-900">{t.opening}</p>
          </div>
        )}

        {deciding && (
          <div className="w-full animate-pulse space-y-3 pt-1" aria-busy="true" data-testid="front-door-skeleton">
            <div className="h-8 w-4/5 rounded-lg bg-slate-100" />
            <div className="h-5 w-full rounded bg-slate-100" />
            <div className="h-5 w-3/5 rounded bg-slate-100" />
            <div className="!mt-8 h-[52px] w-full rounded-xl bg-slate-100" />
            <div className="h-[52px] w-full rounded-xl bg-slate-200" />
          </div>
        )}

        {!verifying && !deciding && (
          <FrontDoor
            key={`${situation}:${hint ?? ''}`}
            lang={lang}
            situation={situation}
            intake={lead && operator ? lead : null}
            operator={operator}
            hint={hint}
            next={nextPath}
            onNoticeChanged={() => setNoticeReload((n) => n + 1)}
            onSignedIn={() => navigate(next, { replace: true })}
          />
        )}

        {!verifying && (
          <p className="mt-8 flex items-center gap-1.5 text-[16px] text-slate-500">
            <Lock className="h-4 w-4 shrink-0" aria-hidden /> {t.secure}
          </p>
        )}
        {/* The app is TendedLoop's; the band above is the building's. */}
        {brand && (
          <p className="mt-3 text-[16px] text-slate-500">
            <b className="font-bold text-slate-800" dir="ltr">
              TendedLoop
            </b>{' '}
            · {PORTAL_NAME[lang]}
          </p>
        )}
      </div>
    </div>
  );
}
